/**
 * Pure display formatters for the shared components. Kept self-contained (no
 * store / app imports) so the components that use them stay store-agnostic and
 * portable.
 *
 * This is the lowest tier, so it holds only what a SHARED component needs —
 * `avatarGradient` for `AvatarChip` and `formatPrice` for the two vehicle
 * surfaces. `-mobile` re-exports both from here and keeps the formatters only
 * its own components use (`formatCountdown`, `formatSaleStart`,
 * `auctionLightName`), which is why there is no second copy of either function
 * in the monorepo.
 */

/** Ordered gradient palette used for hash-assigned avatars. */
export const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #3ee0a4, #2a8a64)',
  'linear-gradient(135deg, #ff9866, #c95a2c)',
  'linear-gradient(135deg, #6ab0ff, #2c6abf)',
  'linear-gradient(135deg, #b88dff, #6a3fc9)',
  'linear-gradient(135deg, #ffd866, #c98a2c)',
];

/**
 * Deterministically pick a gradient for a stable key (an id or a name) so the
 * same entity always gets the same color wherever it is drawn. The app used to
 * hold the only copy of this and every avatar site derived its own inline style
 * from it; `AvatarChip` now owns the derivation so the seed is all a caller
 * passes.
 *
 * @param {string | number | null | undefined} key
 * @returns {string}
 */
export const avatarGradient = (key) => {
  const text = String(key ?? '');
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) | 0;
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

/**
 * Which language money is written in, as a function the application installs.
 *
 * A function rather than a locale string, because the application's locale is
 * reactive: read inside a render or a computed, this registers the dependency,
 * so every figure on screen re-draws when the reader switches language. The
 * default answers English, which is what the library drew before there was a
 * second language and what every consumer that installs nothing still gets —
 * Storybook, the desktop portal, a test mounting a component on its own.
 *
 * It lives here rather than being a prop on each component because `formatPrice`
 * has thirty call sites across three tiers, most of them inside components that
 * are deliberately store-agnostic and have no way to be told. One installation
 * at application boot is the whole mechanism.
 */
let priceLocaleSource = () => 'en-CA';

/**
 * Install the application's locale. `source` is a function (read live, so it
 * follows a language change) or a plain tag.
 */
export const setPriceLocale = (source) => {
  priceLocaleSource = typeof source === 'function' ? source : () => source;
};

/** The locale money is currently written in. */
export const priceLocale = () => priceLocaleSource();

/**
 * ONE money format, per locale, built once each.
 *
 * US dollars, because every amount in this application is one, with the NARROW
 * symbol in every language: a bare `$`. The suffixed form (`13 800 $ US`) was
 * tried in fr-CA to name the dollars and is out, because no screen says which
 * dollars in English either and a suffix nothing else carries is the louder
 * inconsistency. Whole dollars, no cents: nothing in the catalogue or the
 * runtime quotes one.
 *
 * Spacing is whatever `Intl` emits for the locale and is never written by hand.
 * fr-CA puts a no-break space (U+00A0) between the digits and the symbol, and
 * the same U+00A0 between groups of digits ('14 600 $'), not the narrow
 * U+202F fr-FR uses; those code points are the locale's to choose, and a
 * literal in this file would be a second opinion about them.
 */
const PRICE_OPTIONS = Object.freeze({
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'narrowSymbol',
  maximumFractionDigits: 0,
});

/**
 * The same figure compacted, in two tiers.
 *
 * Below a million it is whole thousands: `$103K`, `103 k $`. The decimal costs
 * two characters, and in French both of its separators are no-break spaces that
 * a monospace figure pays a full cell for — `102,5 k $` sets 115px where a 320px
 * phone's stat tile has 90. So the widest figure this tier writes is `999 k $`.
 *
 * From 999,500 up, where whole thousands would round to `1 000 k $`, it is
 * millions to ONE decimal, with a trailing `.0` dropped: `$1M`, `$1.5M`,
 * `1 M $`, `1,5 M $`. Whole millions alone dropped up to $500K (`$1.45M` read
 * `$1M`, `$1.5M` read `$2M`).
 *
 * The tier and the rounding are decided on whole numbers, half up, before Intl
 * sees anything, so no float sits on a boundary: the formats below are only
 * ever handed an exact multiple of a thousand or of a hundred thousand.
 *
 * This is the portal's `compactCurrencyWhole` — the same shape and the same reasoning,
 * as a function rather than a vue-i18n number format, so the library tiers that
 * carry no catalog can reach it too.
 */
const COMPACT_PRICE_OPTIONS = Object.freeze({ ...PRICE_OPTIONS, notation: 'compact' });
// minimumFractionDigits 0 is spelled out: the trailing `.0` is dropped by
// this option, not by an engine default (Node 22's ICU wrote `$1.0M` without it).
const COMPACT_MILLIONS_OPTIONS = Object.freeze({
  ...COMPACT_PRICE_OPTIONS,
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/** Where whole thousands would round up to a thousand thousands. */
const MILLIONS_FROM = 999_500;

/** `whole` rounded half up to a multiple of `step`, on integers only. */
const roundHalfUpTo = (whole, step) => Math.floor((whole + step / 2) / step) * step;

const priceFormats = new Map();
const priceFormatFor = (locale, options, cacheKey) => {
  const key = `${locale}:${cacheKey}`;
  if (!priceFormats.has(key)) {
    priceFormats.set(key, new Intl.NumberFormat(locale, options));
  }
  return priceFormats.get(key);
};

/**
 * Whole-dollar USD in the reader's language: `$24,500` in English, `24 500 $` in
 * French Canadian. Returns `fallback` for null/undefined.
 *
 * Every full amount in the application comes through here — bids, offers,
 * prices, purchases — so no screen can mix two forms of money within a language.
 */
export const formatPrice = (
  value,
  fallback = '—'
) => {
  if (value == null) return fallback;
  const locale = String(priceLocale() ?? 'en-CA');
  return priceFormatFor(locale, PRICE_OPTIONS, 'full').format(Number(value));
};

/**
 * The same money compacted: whole thousands below 999,500 (`$91K`, `91 k $`),
 * millions to one decimal from there (`$1.5M`, `1,5 M $`). Returns `fallback`
 * for null/undefined.
 *
 * For a tile or a summary card ONLY — a figure that has to fit a third of a
 * phone's width. Anywhere a reader is being asked to act on the number, the
 * full amount is the one to draw.
 */
export const formatCompactPrice = (
  value,
  fallback = '—'
) => {
  if (value == null) return fallback;
  const locale = String(priceLocale() ?? 'en-CA');
  const number = Number(value);
  if (!Number.isFinite(number)) return priceFormatFor(locale, COMPACT_PRICE_OPTIONS, 'compact').format(number);
  // Whole dollars first, half up and symmetric about zero, so a negative
  // figure rounds as its positive twin does and keeps its sign.
  const whole = Math.floor(Math.abs(number) + 0.5);
  const sign = number < 0 && whole > 0 ? -1 : 1;
  if (whole >= MILLIONS_FROM) {
    return priceFormatFor(locale, COMPACT_MILLIONS_OPTIONS, 'compact-millions')
      .format(sign * roundHalfUpTo(whole, 100_000));
  }
  const rounded = whole < 1_000 ? whole : roundHalfUpTo(whole, 1_000);
  return priceFormatFor(locale, COMPACT_PRICE_OPTIONS, 'compact').format(sign * rounded);
};

/**
 * Up-to-two-letter initials for a person's or dealership's name. Returns `·`
 * for empty input. Companion to AvatarChip: the chip owns the colour; this owns
 * the letters.
 *
 * @param {string | null | undefined} name
 * @returns {string}
 */
export const initialsOf = (name) => {
  if (!name) return '·';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '·';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
